import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const LABELS = {
  probleme: "Problème",
  idee: "Idée",
  suggestion: "Suggestion",
  info: "Demande d'info",
};

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    api.get("/feedback").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => {
    if (filter === "all") return true;
    if (filter === "open") return i.status === "open";
    if (filter === "answered") return i.status === "answered";
    return i.type === filter;
  });

  const send = async () => {
    if (!reply.trim()) return toast.error("Saisissez une réponse");
    setBusy(true);
    try {
      await api.patch(`/feedback/${selected.id}/reply`, { reply });
      toast.success("Réponse envoyée par email à l'habitant.");
      setSelected(null); setReply(""); load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-12">
      <header>
        <div className="phenix-eyebrow mb-3">Modération</div>
        <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
          Remontées <em className="text-pink-500">citoyennes.</em>
        </h1>
      </header>

      <div className="flex flex-wrap gap-x-8 gap-y-3 border-y border-cream-200 py-5">
        {[
          { v: "all", label: "Toutes" },
          { v: "open", label: "En attente" },
          { v: "answered", label: "Répondues" },
          { v: "probleme", label: "Problèmes" },
          { v: "idee", label: "Idées" },
          { v: "suggestion", label: "Suggestions" },
          { v: "info", label: "Infos" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`filter-${f.v}`}
            className={`text-[12px] uppercase tracking-[0.18em] transition-colors ${
              filter === f.v ? "text-pink-500 font-medium" : "text-plum-700/70 hover:text-ink-900"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div data-testid="admin-feedback-table">
        {filtered.length === 0 ? (
          <div className="border border-dashed border-cream-200 p-16 text-center text-plum-700/50 italic">
            Aucune remontée.
          </div>
        ) : (
          <div className="divide-y divide-cream-200 border-y border-cream-200">
            {filtered.map((f, i) => (
              <div key={f.id} className="grid lg:grid-cols-12 gap-6 py-8">
                <div className="lg:col-span-1 font-mono text-[11px] text-plum-700/50">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="lg:col-span-2">
                  <div className="phenix-eyebrow mb-1.5">{LABELS[f.type]}</div>
                  <div className="text-[11px] text-plum-700/50 italic capitalize">{f.user_role}</div>
                </div>
                <div className="lg:col-span-7">
                  <h3 className="heading-display text-base text-plum-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-plum-700 leading-relaxed mb-3">{f.content}</p>
                  <div className="text-[11px] text-plum-700/70">
                    <span className="italic">Pseudo public :</span> <strong className="font-mono">{f.user_pseudo}</strong>
                    <span className="mx-2 text-plum-700/40">·</span>
                    <span className="italic">Identité :</span> <strong>{f.author?.name}</strong> ({f.author?.email})
                  </div>
                  {f.admin_reply && (
                    <div className="mt-4 pl-4 border-l-2 border-clay-500">
                      <div className="phenix-eyebrow text-pink-500 mb-1">Votre réponse</div>
                      <p className="text-sm text-plum-900 italic">{f.admin_reply}</p>
                    </div>
                  )}
                </div>
                <div className="lg:col-span-2 flex flex-col items-end gap-3">
                  {f.status === "answered" ? (
                    <span className="text-[11px] uppercase tracking-widest text-pink-500">Répondue</span>
                  ) : (
                    <>
                      <span className="text-[11px] uppercase tracking-widest text-plum-700/50 italic">En attente</span>
                      <Button
                        size="sm"
                        onClick={() => { setSelected(f); setReply(""); }}
                        className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-9 group"
                        data-testid={`admin-reply-btn-${f.id}`}
                      >
                        Répondre
                        <ArrowUpRight size={12} className="ml-1.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-cream-50 border-cream-200 rounded-xl">
          <DialogHeader>
            <DialogTitle className="heading-display text-base text-plum-900">
              Répondre à : <em className="editorial-italic">{selected?.title}</em>
            </DialogTitle>
          </DialogHeader>
          <Textarea
            rows={6} value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder="Votre réponse officielle..."
            data-testid="admin-reply-textarea"
            className="rounded-xl border-cream-200 bg-transparent"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)} className="rounded-xl">Annuler</Button>
            <Button onClick={send} disabled={busy}
              className="btn-pill bg-plum-600 hover:bg-plum-700 text-white"
              data-testid="admin-reply-send-btn">
              Envoyer
              <ArrowUpRight size={14} className="ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
